import { podeFiscalizar } from '../../auth/permissoes'
import { useSessao } from '../../auth/contexto'
import { useColetarAgora, useSituacaoDaColeta } from '../../api/queries/repositorios'
import { Botao } from '../../components/Botao'
import { AvisoDeErro, Esqueleto } from '../../components/Estados'
import { dataHora } from '../../lib/datas'
import { TEXTOS } from '../../lib/glossario'
import type { Sincronizacao } from '../../types/repositorios'
import { BlocoDoDiario } from './BlocoDoDiario'

/**
 * De quando é o dado desta tela, e com que credencial ele foi lido.
 *
 * Sem isto, uma coleta que falhou em silêncio vira um mês que parece sem atividade. A última
 * rodada aparece com o resultado dela (ou o motivo da falha), e o fiscal pode pedir uma agora — em
 * segundo plano, porque a primeira lê meses de história. Abaixo, a cópia do Diário de Bordo — de
 * onde vem quem é equipe interna.
 *
 * @returns o cartão da coleta
 */
export function SituacaoDaColeta() {
  const t = TEXTOS.repositorios.coleta
  const { usuario } = useSessao()
  const { data, isPending, isError, error } = useSituacaoDaColeta()
  const coletar = useColetarAgora()

  return (
    <section className="cartao space-y-3 p-5" aria-label={t.titulo}>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900">{t.titulo}</h2>
          <p className="mt-1 text-sm text-slate-500">{t.periodicidade}</p>
        </div>
        {data?.disponivel && podeFiscalizar(usuario) && (
          <Botao
            aparencia="secundario"
            tamanho="pequeno"
            onClick={() => coletar.mutate()}
            disabled={coletar.isPending || data.emAndamento}
          >
            {data.emAndamento ? t.emAndamento : t.coletarAgora}
          </Botao>
        )}
      </header>
      {isPending && <Esqueleto linhas={2} />}
      {isError && <AvisoDeErro erro={error} />}
      {coletar.isError && <AvisoDeErro erro={coletar.error} />}
      {coletar.data && (
        <p className="text-sm text-slate-700">{coletar.data.iniciada ? t.coletaPedida : t.jaEmAndamento}</p>
      )}
      {data && (
        <dl className="grid gap-2 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-xs font-semibold text-slate-500">{t.credencial}</dt>
            <dd className="text-slate-800">{data.credencial ?? '—'}</dd>
            {!data.disponivel && (
              <dd className="mt-1 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-slate-800">
                {t.indisponivel} {data.motivoIndisponivel}
              </dd>
            )}
          </div>
          <div>
            <dt className="text-xs font-semibold text-slate-500">{t.ultima}</dt>
            <dd className="text-slate-800">
              <Rodada rodada={data.ultimaSincronizacao} />
            </dd>
          </div>
        </dl>
      )}
      {data?.diarioDeBordo && <BlocoDoDiario diario={data.diarioDeBordo} />}
    </section>
  )
}

function Rodada({ rodada }: { rodada?: Sincronizacao }) {
  const t = TEXTOS.repositorios.coleta
  if (!rodada) {
    return <>{t.nenhuma}</>
  }
  if (rodada.situacao === 'EM_ANDAMENTO') {
    return (
      <>
        {t.emAndamento} ({dataHora(rodada.iniciadaEm)})
      </>
    )
  }
  if (rodada.situacao === 'FALHOU') {
    return (
      <span className="text-red-800">
        {t.falhou} ({dataHora(rodada.concluidaEm ?? rodada.iniciadaEm)}): {rodada.erro}
      </span>
    )
  }
  return (
    <>
      {dataHora(rodada.concluidaEm)} — {t.resultado(rodada.repositorios, rodada.commitsNovos, rodada.commitsDetalhados)}
      {rodada.disparadaPor && <span className="text-slate-500"> ({rodada.disparadaPor})</span>}
    </>
  )
}
